import React, { useEffect, useMemo, useRef, useState } from "react";
import { searchService } from "../../services/api";
import { useDebounce } from "../../hooks/useDebounce";
import { LoupeGlyph } from "./glyphs";

// Internal club selector for the create form.
//
// The chosen club is stored on the tender (required FK) but is NEVER shown in
// the feed or on any public surface (Brief Rule 4). Source = the platform's
// existing general (Typesense) club search, so EVERY club is selectable, not
// just registered ones (DoD 23 / D-012, Marin: "alle Vereine", one list). Each
// hit is Wyscout-based { wyscout_id, name, country{…}, image_path,
// platform_data:{ exists, club_id } }. `platform_data.exists` tells whether the
// club is already on the platform:
//   • on-platform → create sends club_id (platform_data.club_id)
//   • catalog      → create sends wyscout_club_id (wyscout_id); the backend
//                    creates an ownerless club (idempotent).
//
// V2 UI rebuild (doc 02 "Client club" boards): the SEARCH WIRING above is
// test-pinned and unchanged — only the visual shell is rebuilt: 44px search
// field with the verbatim loupe, result rows with 30px club marks, matched-
// prefix highlight, "On platform" / "From catalog" badges, dashed catalog
// footnote, and the selected-state row with the privacy note.
const countryName = (c) => (c && typeof c === "object" ? c.name : c) || "";

// Verbatim artboard loupe (RULE 2) — from the shared glyph set.
const IcSearch = () => <LoupeGlyph size={14} />;

// Club mark: logo when available, else initials on a deterministic dark tone
// (artboard rows use colored initial tiles, 9-10px radius squares).
const MARK_COLORS = ["#0F766E", "#1E40AF", "#A16207", "#334155", "#0E7490", "#9333EA"];
const markColor = (name) => {
  let h = 0;
  for (const ch of name || "") h = (h * 31 + ch.charCodeAt(0)) % 997;
  return MARK_COLORS[h % MARK_COLORS.length];
};
const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const ClubMark = ({ club, size = 30, radius = 9 }) => {
  const src = club.image_path || club.logo;
  const style = { width: size, height: size, borderRadius: radius };
  if (src) {
    return <img src={src} alt={club.name} className="flex-none object-cover" style={style} />;
  }
  return (
    <span
      className="flex-none inline-flex items-center justify-center text-white text-[11px] font-extrabold"
      style={{ ...style, background: markColor(club.name) }}
    >
      {initials(club.name)}
    </span>
  );
};

// Matched-prefix highlight as drawn (#BFDBFE, 3px radius).
const Highlight = ({ name, query }) => {
  const i = query ? (name || "").toLowerCase().indexOf(query.toLowerCase()) : -1;
  if (i < 0) return name;
  return (
    <>
      {name.slice(0, i)}
      <b className="bg-[#BFDBFE] rounded-[3px] px-px">{name.slice(i, i + query.length)}</b>
      {name.slice(i + query.length)}
    </>
  );
};

// "On platform" (green) / "From catalog" (grey) source badge.
const SourceBadge = ({ onPlatform, small }) => (
  <span
    className={`flex-none font-extrabold rounded-full px-2 py-0.5 ${
      small ? "text-[10px]" : "text-[10.5px] font-bold"
    } ${onPlatform ? "bg-[#ECFDF5] text-[#047857]" : "bg-[#F1F5F9] text-[#475569]"}`}
  >
    {onPlatform ? "On platform" : "From catalog"}
  </span>
);

// Meta line: country, plus the league tier when the hit carries one.
const clubMeta = (club) =>
  [countryName(club.country), club.league_tier].filter(Boolean).join(" · ");

// `label` / `helpText` stay as props for API compatibility (empty hides them —
// the agent form's card already carries the "Client club" heading).
const ClubPicker = ({
  value,
  onChange,
  error,
  label = "Club",
  required = true,
  helpText = "",
}) => {
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounced = useDebounce(query, 300);
  const boxRef = useRef(null);

  useEffect(() => {
    const onClickOutside = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      setLoading(true);
      const res = await searchService.search({
        type: "clubs",
        query: debounced,
        per_page: 20,
      });
      if (!cancelled) {
        setResults(res.success ? res.data?.results?.clubs || [] : []);
        setLoading(false);
      }
    };
    if (open && debounced.trim().length >= 2) {
      run();
    } else {
      setResults([]);
      setLoading(false);
    }
    return () => {
      cancelled = true;
    };
  }, [debounced, open]);

  const selectedMeta = useMemo(() => (value ? clubMeta(value) : ""), [value]);

  return (
    <div ref={boxRef}>
      {label && (
        <label className="block text-[12.5px] font-semibold text-[#334155] mb-1.5">
          {label} {required && <span className="text-[#DC2626]">*</span>}
        </label>
      )}
      {helpText && <p className="text-[11.5px] text-[#94A3B8] mb-2">{helpText}</p>}

      {value ? (
        <>
          <div className="flex items-center gap-2.5 border border-[#E2E8F0] rounded-[10px] bg-[#F8FAFC] px-3 py-2.5">
            <ClubMark club={value} size={34} radius={10} />
            <div className="min-w-0 flex-1">
              <div className="text-[13.5px] font-bold text-[#0F172A] truncate">{value.name}</div>
              {selectedMeta && (
                <div className="text-[11.5px] text-[#64748B] truncate">{selectedMeta}</div>
              )}
            </div>
            <SourceBadge onPlatform={value.platform_data?.exists === true} />
            <button
              type="button"
              onClick={() => {
                onChange(null);
                setOpen(true);
                setQuery("");
              }}
              className="flex-none text-[12.5px] font-semibold text-[#1B6FE0] hover:text-[#2283FF]"
            >
              Change
            </button>
          </div>
          <p className="mt-2 text-[11.5px] text-[#94A3B8]">
            Visible only to your agency and the club itself · never in the
            marketplace.
          </p>
        </>
      ) : (
        <>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 inline-flex text-[#94A3B8]">
              <IcSearch />
            </span>
            <input
              type="text"
              value={query}
              onFocus={() => setOpen(true)}
              onChange={(e) => {
                setQuery(e.target.value);
                setOpen(true);
              }}
              placeholder="Search for a club"
              className={`h-11 w-full pl-9 pr-3 rounded-[9px] border bg-white text-[13.5px] outline-none placeholder:text-[#94A3B8] focus:border-[1.5px] focus:border-[#2283FF] focus:shadow-[0_0_0_3px_#E8F1FF] ${
                error ? "border-red-500" : "border-[#E2E8F0]"
              }`}
            />
            {open && (
              <div className="absolute z-20 mt-1.5 w-full bg-white border border-[#E2E8F0] rounded-[10px] shadow-[0_12px_32px_rgba(15,23,42,.14)] overflow-hidden">
                <div className="max-h-64 overflow-y-auto">
                  {debounced.trim().length < 2 ? (
                    <div className="px-[13px] py-3 text-[13px] text-[#94A3B8]">
                      Type at least 2 letters to search.
                    </div>
                  ) : loading ? (
                    <div className="px-[13px] py-3 text-[13px] text-[#94A3B8]">Loading…</div>
                  ) : results.length === 0 ? (
                    <div className="px-[13px] py-3 text-[13px] text-[#94A3B8]">
                      No clubs found.
                    </div>
                  ) : (
                    results.map((club, idx) => {
                      const onPlatform = club.platform_data?.exists === true;
                      const meta = clubMeta(club);
                      return (
                        <button
                          key={`ws-${club.wyscout_id}`}
                          type="button"
                          // Full club name as the accessible name — the visual
                          // label is split by the matched-prefix highlight.
                          aria-label={club.name}
                          onClick={() => {
                            onChange(club);
                            setOpen(false);
                          }}
                          className={`w-full flex items-center gap-2.5 px-[13px] py-2.5 text-left hover:bg-[#E8F1FF]/60 ${
                            idx > 0 ? "border-t border-[#F1F5F9]" : ""
                          }`}
                        >
                          <ClubMark club={club} />
                          <span className="flex-1 min-w-0">
                            <span className="block text-[13px] font-bold text-[#0F172A] truncate">
                              <Highlight name={club.name} query={debounced.trim()} />
                            </span>
                            {meta && (
                              <span className="block text-[11px] text-[#64748B] truncate">
                                {meta}
                              </span>
                            )}
                          </span>
                          <SourceBadge onPlatform={onPlatform} small />
                        </button>
                      );
                    })
                  )}
                </div>
                <div className="px-[13px] py-[9px] border-t border-dashed border-[#EEF2F7] text-[11px] text-[#94A3B8]">
                  Every club in our database is selectable · platform members and
                  the full catalog. Catalog clubs are added automatically, no
                  account needed.
                </div>
              </div>
            )}
          </div>
          <p className="mt-2 text-[11.5px] text-[#94A3B8]">
            Type to search · pick a result to lock it in. Visible only to your
            agency and the club itself.
          </p>
        </>
      )}

      {error && <p className="mt-1 text-sm text-red-500">{error}</p>}
    </div>
  );
};

export default ClubPicker;
